import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Skeleton, Stack } from "@mui/material";
import SectionWrapper from "../../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { CurrentCommunicationFormValues } from "./interface";
import useGetCurrentCommunication from "hooks/api/client/useGetCurrentCommunication";
import useUpsertCurrentCommunication from "hooks/api/client/useUpsertCurrentCommunication";

const EMPTY_VALUES: CurrentCommunicationFormValues = {
  primaryCommunicationModality: "",
  communicationProficiency: "",
  communicationFunctionLevel: "",
};

export const CurrentCommunication: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { currentCommunication, refetchCurrentCommunication, isFetched: isCurrentCommunicationFetched } =
    useGetCurrentCommunication(clientIdNum);

  const { upsertCurrentCommunicationMutation, isUpsertCurrentCommunicationLoading } =
    useUpsertCurrentCommunication({
      onSuccess: () => {
        refetchCurrentCommunication();
        setEditMode(false);
      },
    });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<CurrentCommunicationFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<CurrentCommunicationFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (currentCommunication) {
      const values: CurrentCommunicationFormValues = {
        primaryCommunicationModality: currentCommunication.primary_modality || "",
        communicationProficiency: currentCommunication.proficiency || "",
        communicationFunctionLevel: currentCommunication.function_level || "",
      };
      setFormValues(values);
      setInitialValues(values);
    }
  }, [currentCommunication]);

  const handleChange =
    (field: keyof CurrentCommunicationFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    upsertCurrentCommunicationMutation({
      clientId: clientIdNum,
      payload: {
        primary_modality: formValues.primaryCommunicationModality || null,
        proficiency: formValues.communicationProficiency || null,
        function_level: formValues.communicationFunctionLevel || null,
      },
      isNew: isCurrentCommunicationFetched && !currentCommunication,
    });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isUpsertCurrentCommunicationLoading;

  if (!isCurrentCommunicationFetched) {
    return (
      <SectionWrapper
        title="Current Communication"
        description="Indicate how the client currently communicates, including primary modality, proficiency with that modality, and level of communication."
        hideButtons
      >
        <Stack gap={2}>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
        </Stack>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Current Communication"
      description="Indicate how the client currently communicates, including primary modality, proficiency with that modality, and level of communication."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default CurrentCommunication;
